// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import * as Location from 'expo-location';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';

import { combineFixes, type ClusterFix } from '@/services/positionCluster';

/** Fixes older than this no longer describe where the mapper is standing. */
const FIX_WINDOW_MS = 5000;

/** A single fix worse than this is dropped before averaging. */
const MAX_FIX_ACCURACY_M = 25;

/** The averaged position has to be at least this sharp before a stake can be marked. */
export const MAX_STAKE_ACCURACY_M = 8;

/** Below this calibration level the compass is not trusted; same bar as the hole view. */
const MIN_HEADING_ACCURACY = 1;

export type StakeFix = {
  lat: number;
  lon: number;
  /** Standard error of the averaged position, metres. */
  accuracyMeters: number;
};

export type StakeHeading = {
  /** Degrees clockwise from north. */
  degrees: number;
  /** The platform's calibration level, 0–3. */
  accuracy: number;
};

export type StakeCaptureState = {
  fix: StakeFix | null;
  heading: StakeHeading | null;
  permission: 'unknown' | 'granted' | 'denied';
  /** Whether the current fix is sharp enough to mark a stake. */
  ready: boolean;
  /** Freezes the fix and heading as they are this instant, for the moment the camera opens. */
  snapshot: () => { fix: StakeFix; heading: StakeHeading | null } | null;
};

/**
 * Where the phone is and which way it faces, for recording a boundary stake.
 *
 * The position is the last five seconds of fixes combined (see `combineFixes`), because a
 * phone held still still wanders, and one fix is whichever wander it was on. The heading is
 * the compass as it stands, or null when it is not calibrated — a stake can be marked without
 * one, but then the line built from it cannot tell which side is out.
 *
 * Native only: the web has no compass, and a browser's geolocation is not the instrument.
 */
export const useStakeCapture = ({ enabled }: { enabled: boolean }): StakeCaptureState => {
  const [fix, setFix] = useState<StakeFix | null>(null);
  const [heading, setHeading] = useState<StakeHeading | null>(null);
  const [permission, setPermission] = useState<StakeCaptureState['permission']>('unknown');
  const fixesRef = useRef<ClusterFix[]>([]);
  const fixRef = useRef<StakeFix | null>(null);
  const headingRef = useRef<StakeHeading | null>(null);

  useEffect(() => {
    if (!enabled || Platform.OS === 'web') {
      return undefined;
    }

    let cancelled = false;
    let position: Location.LocationSubscription | null = null;
    let compass: Location.LocationSubscription | null = null;

    const begin = async () => {
      const { status } = await Location.requestForegroundPermissionsAsync();
      if (cancelled) return;
      if (status !== 'granted') {
        setPermission('denied');
        return;
      }
      setPermission('granted');

      try {
        position = await Location.watchPositionAsync(
          { accuracy: Location.Accuracy.BestForNavigation, timeInterval: 1000, distanceInterval: 0 },
          (reading) => {
            if (cancelled) return;
            const now = reading.timestamp || Date.now();
            const accuracy = reading.coords.accuracy ?? null;
            fixesRef.current = [
              ...fixesRef.current.filter((f) => now - f.ts <= FIX_WINDOW_MS),
              { lat: reading.coords.latitude, lon: reading.coords.longitude, accuracy, speed: reading.coords.speed ?? null, ts: now },
            ];
            const fit = combineFixes(fixesRef.current, MAX_FIX_ACCURACY_M);
            const next = fit
              ? { lat: fit.lat, lon: fit.lon, accuracyMeters: fit.accuracy ?? MAX_FIX_ACCURACY_M }
              : null;
            fixRef.current = next;
            setFix(next);
          },
        );
      } catch {
        if (!cancelled) setFix(null);
      }

      try {
        compass = await Location.watchHeadingAsync((reading) => {
          if (cancelled) return;
          if (reading.accuracy < MIN_HEADING_ACCURACY) {
            headingRef.current = null;
            setHeading(null);
            return;
          }
          const degrees = reading.trueHeading >= 0 ? reading.trueHeading : reading.magHeading;
          const next = { degrees: ((degrees % 360) + 360) % 360, accuracy: reading.accuracy };
          headingRef.current = next;
          setHeading(next);
        });
      } catch {
        if (!cancelled) setHeading(null);
      }
    };

    void begin();

    return () => {
      cancelled = true;
      try {
        position?.remove();
        compass?.remove();
      } catch {
        // expo-location's teardown can throw; unmounting must not take the screen with it.
      }
    };
  }, [enabled]);

  const snapshot = useCallback(() => {
    const current = fixRef.current;
    if (!current || current.accuracyMeters > MAX_STAKE_ACCURACY_M) return null;
    return { fix: current, heading: headingRef.current };
  }, []);

  return {
    fix,
    heading,
    permission,
    ready: fix !== null && fix.accuracyMeters <= MAX_STAKE_ACCURACY_M,
    snapshot,
  };
};
